/**
 * Minimal toast system: a context provider, a `useToast` hook, and a portalled
 * viewport. Built in-house rather than pulling a library because the only need
 * today is ephemeral "copied" / "downloaded" feedback for sharing.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/cn'
import { glassSurface } from '@/lib/glassSurface'

type ToastTone = 'success' | 'error' | 'info'

type Toast = {
  id: number
  message: string
  tone: ToastTone
}

type ToastContextValue = {
  /** Shows a toast. Defaults to the `info` tone and a ~2.6s lifetime. */
  showToast: (message: string, tone?: ToastTone) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const TOAST_LIFETIME_MS = 2600

export function ToastProvider({ children }: PropsWithChildren) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(1)

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const showToast = useCallback(
    (message: string, tone: ToastTone = 'info') => {
      const id = nextId.current++
      setToasts((prev) => [...prev, { id, message, tone }])
      window.setTimeout(() => dismiss(id), TOAST_LIFETIME_MS)
    },
    [dismiss],
  )

  const value = useMemo(() => ({ showToast }), [showToast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <ToastViewport toasts={toasts} onDismiss={dismiss} />,
          document.body,
        )}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within a ToastProvider')
  return ctx
}

const TONE_ACCENT: Record<ToastTone, string> = {
  success: 'bg-[#00A63E]',
  error: 'bg-red-500',
  info: 'bg-[#E0A263]',
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Toast[]
  onDismiss: (id: number) => void
}) {
  if (toasts.length === 0) return null
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[200] flex flex-col items-center gap-2 px-4">
      {toasts.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onDismiss(t.id)}
          className={cn(
            'pointer-events-auto flex w-full max-w-[360px] items-center gap-3 rounded-xl px-4 py-3 text-left text-sm text-white shadow-2xl',
            'animate-[toast-in_180ms_ease-out]',
            glassSurface,
          )}
        >
          <span
            className={cn('h-2 w-2 shrink-0 rounded-full', TONE_ACCENT[t.tone])}
          />
          <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
            {t.message}
          </span>
        </button>
      ))}
    </div>
  )
}
